if (!customElements.get('sticky-buy-buttons')) {
  customElements.define(
    'sticky-buy-buttons',
    class StickyBuyButtons extends HTMLElement {
      constructor() {
        super();
        this.onStickyButtonClick = this.onStickyButtonClick.bind(this);
        this.syncState = this.syncState.bind(this);
      }

      connectedCallback() {
        this.sectionId = this.dataset.sectionId;

        // Move the bar out to <body> so `position: fixed` is anchored to the
        // viewport. Inside the section it can be trapped by a transformed
        // ancestor (e.g. `.product__info-wrapper.scroll-trigger`), which also
        // clips it. Re-running connectedCallback after the move is expected.
        if (this.parentElement !== document.body) {
          document
            .querySelectorAll(`sticky-buy-buttons[data-section-id="${this.sectionId}"]`)
            .forEach((el) => el !== this && el.remove());
          this.sourceSection =
            this.closest('.shopify-section')?.id.replace('shopify-section-', '') || null;
          document.body.appendChild(this);
          return;
        }

        // The real add-to-cart button. Only the standard add-to-cart flow renders
        // this id (pre-order / sign-up / gated "Login to buy" states do not), so
        // its absence means the sticky bar should not exist.
        this.realButton = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
        if (!this.realButton) {
          this.remove();
          return;
        }

        // Theme editor: the bar now lives outside its section, so remove it when
        // that section is removed/reloaded.
        this.onSectionUnload = (event) => {
          if (!this.sourceSection || event.detail.sectionId === this.sourceSection) this.remove();
        };
        document.addEventListener('shopify:section:unload', this.onSectionUnload);
        this.stickyButton = this.querySelector('.sticky-buy-buttons__submit');
        this.stickyButtonText = this.stickyButton.querySelector('span');
        this.stickyPrice = this.querySelector(`#StickyPrice-${this.sectionId}`);
        this.realPrice = document.getElementById(`price-${this.sectionId}`);

        this.stickyButton.addEventListener('click', this.onStickyButtonClick);

        // Show the bar when the real button has scrolled above the viewport.
        this.visibilityObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              this.buttonScrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
              this.syncState();
            });
          },
          { threshold: 0 }
        );
        this.visibilityObserver.observe(this.realButton);

        // Mirror disabled / loading / label state and reflect variant price changes.
        this.mutationObserver = new MutationObserver(this.syncState);
        this.mutationObserver.observe(this.realButton, {
          attributes: true,
          attributeFilter: ['disabled', 'aria-disabled', 'class'],
          childList: true,
          subtree: true,
          characterData: true,
        });
        if (this.realPrice) {
          this.mutationObserver.observe(this.realPrice, {
            childList: true,
            subtree: true,
            characterData: true,
          });
        }

        this.syncState();
      }

      disconnectedCallback() {
        this.visibilityObserver?.disconnect();
        this.mutationObserver?.disconnect();
        this.stickyButton?.removeEventListener('click', this.onStickyButtonClick);
        if (this.onSectionUnload) document.removeEventListener('shopify:section:unload', this.onSectionUnload);
      }

      get isPurchasable() {
        return (
          !this.realButton.hasAttribute('disabled') &&
          this.realButton.getAttribute('aria-disabled') !== 'true'
        );
      }

      onStickyButtonClick() {
        if (!this.isPurchasable) return;
        this.realButton.click();
      }

      syncState() {
        // Keep price + label in sync with the real add-to-cart button.
        if (this.realPrice && this.stickyPrice && this.stickyPrice.innerHTML !== this.realPrice.innerHTML) {
          this.stickyPrice.innerHTML = this.realPrice.innerHTML;
        }

        const realText = this.realButton.querySelector('span')?.textContent.trim();
        if (realText && this.stickyButtonText.textContent.trim() !== realText) {
          this.stickyButtonText.textContent = realText;
        }

        const loading = this.realButton.classList.contains('loading');
        this.stickyButton.classList.toggle('loading', loading);
        this.stickyButton.toggleAttribute('disabled', !this.isPurchasable);

        // Only show for a purchasable standard add-to-cart button that has been
        // scrolled past.
        const visible = this.buttonScrolledPast === true && this.isPurchasable;
        this.classList.toggle('sticky-buy-buttons--visible', visible);
        this.setAttribute('aria-hidden', visible ? 'false' : 'true');
        this.toggleAttribute('inert', !visible);
      }
    }
  );
}
